/**
 * AthenaCognitiveSpace v3.1 — Refinamento de comportamento
 *
 * Arquitetura idêntica à v3. Mudanças: somente comportamento.
 * - CodeGrid scan: leitura linha por linha com digitação progressiva
 * - HUD panels: typewriter linha a linha + highlight de conclusão por linha
 * - Holos/barras: nome do processo, flash verde ao concluir, reação das partículas
 * - Ritmo irregular: estados quiet / burst / steady com transições aleatórias
 */
import { memo, useEffect, useRef } from 'react';

// A cena mantém a malha neural, partículas, brilho e formas geométricas.
// Os quadros técnicos (código, telemetria e janelas de processo) ficam
// desligados para uma experiência visual mais limpa.
const SHOW_TECHNICAL_PANELS = false;

// ── Definições de blocos de código vivo — Camada 1 ───────────────────────────
// Cada bloco representa uma operação cognitiva real da Athena.
// 95% ciano, acento âmbar apenas em TOOL. Verde exclusivo no status de conclusão.
const BLOCK_DEFS = [
  {
    type: 'PYTHON', file: 'mem_retrieve.py', color: 'c',
    lines: [
      'import numpy as np',
      'from memory.vector import search',
      'from memory.graph import Graph',
      '',
      'def retrieve_memory(query):',
      '  embedding = embed(query)',
      '  results = search(embedding)',
      '  return results',
      '',
      'ctx = retrieve_memory(user_query)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'RAG', file: 'context_builder.py', color: 'c',
    lines: [
      'from rag.pipeline import build_context',
      'from rag.ranker import rank',
      '',
      'docs = build_context(ctx)',
      'ranked = rank(docs, user_query)',
      'final_ctx = ranked[:5]',
      '',
      'send_to_llm(final_ctx)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'EMBEDDING', file: 'vector.create', color: 'c',
    lines: [
      'text_chunks = split(text)',
      '',
      'vectors = []',
      'for chunk in text_chunks:',
      '  vec = embed(chunk)',
      '  vectors.append(vec)',
      '',
      'save(vectors)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'JSON', file: 'payload.json', color: 'c',
    lines: [
      '{',
      '  "intent": "analyze_doc",',
      '  "confidence": 0.92,',
      '  "sources": 12,',
      '  "language": "pt-BR",',
      '  "mode": "deep_reasoning",',
      '  "stream": true',
      '}',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'TOOL', file: 'browser.py', color: 'am',
    lines: [
      'async def search_web(query):',
      '  browser = await launch()',
      '  page = await browser.new_page()',
      '  await page.goto("https://...")',
      '  results = await extract(page)',
      '  await browser.close()',
      '  return results',
    ],
    run: 'executando...', done: 'concluído ✓',
  },
  {
    type: 'SHELL', file: 'terminal.sh', color: 'c',
    lines: [
      '$ python agent.py --mode deep',
      '$ loading models...',
      '$ connecting to memory...',
      '$ warmup 73%',
      '$ system ready.',
    ],
    run: 'digitando...', done: 'executado ✓',
  },
  {
    type: 'GRAPH', file: 'knowledge.graph', color: 'c',
    lines: [
      'import networkx as nx',
      'g = nx.DiGraph()',
      'g.add_node("Athena")',
      'g.add_edge("Athena", "Memory")',
      'g.add_edge("Memory", "Insight")',
      'g.add_edge("Insight", "Action")',
      '',
      'analyze_centrality(g)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'SQL', file: 'query.sql', color: 'c',
    lines: [
      'SELECT',
      '  source, score, content',
      'FROM memories',
      'WHERE score > 0.78',
      'ORDER BY score DESC',
      'LIMIT 10;',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'PLANNER', file: 'task.plan', color: 'c',
    lines: [
      '1. entender a intenção',
      '2. recuperar contexto',
      '3. buscar informações',
      '4. analisar dados',
      '5. gerar resposta',
      '6. validar e concluir',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'LOG', file: 'system.log', color: 'c',
    lines: null, // gerado em runtime com timestamps reais
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'VECTOR', file: 'faiss.search', color: 'c',
    lines: [
      'idx = faiss.IndexFlatL2(1536)',
      'idx.add(embeddings)',
      '',
      'D, I = idx.search(q, k=10)',
      'hits = [docs[i] for i in I[0]]',
      'return rerank(hits)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'CHAIN', file: 'chain_of_thought.py', color: 'c',
    lines: [
      'def reason(ctx, goal):',
      '  steps = decompose(goal)',
      '  for step in steps:',
      '    r = execute(step, ctx)',
      '    ctx.update(r)',
      '  return synthesize(ctx)',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'TOKENIZER', file: 'tokenize.py', color: 'c',
    lines: [
      'tokens = tok.encode(text)',
      'n = len(tokens)',
      '',
      'if n > MAX_CTX:',
      '  tokens = tokens[-MAX_CTX:]',
      '  text = tok.decode(tokens)',
      '',
      'return text, n',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
  {
    type: 'ATTENTION', file: 'attention.py', color: 'c',
    lines: [
      'Q = W_q @ x',
      'K = W_k @ x',
      'V = W_v @ x',
      '',
      'scale = math.sqrt(d_k)',
      'attn = softmax(Q @ K.T / scale)',
      'out = attn @ V',
    ],
    run: 'digitando...', done: 'concluído ✓',
  },
];

// ── Nomes de processo para holos ─────────────────────────────────────────────
const PROCESS_NAMES = [
  'MEMORY_SCAN', 'CTX_BUILD',  'SEM_SEARCH', 'VALIDATE',
  'INFERENCE',   'RANK_DOCS',  'EMBED_VEC',  'CHAIN_RUN',
  'GRAPH_TRAV',  'TOKENIZE',   'RETRIEVE',   'STREAM',
  'FACT_CHECK',  'ATTENTION',  'BEAM_RANK',  'DECODE_TOK',
];

// ── HUD panel data ────────────────────────────────────────────────────────────
const HUD_FUNCS = [
  'ctx.retrieve(uid)', 'embed.cosine(q,d)', 'chain.think(ctx)',
  'mem.search(q)',     'llm.stream(p)',     'tok.encode(t)',
  'graph.trav(node)', 'bm25.rank(docs)',   'enc.fwd(x,mask)',
  'dec.gen(hidden)',  'attn.self(x)',      'softmax(logits)',
  'intent.cls(tks)',  'retriev.get(q)',    'sched.step(ep)',
  'optim.zero_g()',   'loss.bce(p,y)',     'ctx.slice(-8)',
  'faiss.search(q)',  'torch.no_grad()',   'F.cross_ent()',
];
const HUD_STATUS = ['ACTIVE', 'READY', 'PROC', 'SYNC', 'WAIT'];

// ── Geometry helpers ───────────────────────────────────────────────────────────
function hexPath(ctx, cx, cy, r, a) {
  for (let i = 0; i < 6; i++) {
    const th = (Math.PI/3)*i + a;
    if (i===0) ctx.moveTo(cx+r*Math.cos(th), cy+r*Math.sin(th));
    else        ctx.lineTo(cx+r*Math.cos(th), cy+r*Math.sin(th));
  }
  ctx.closePath();
}
function diamondPath(ctx, cx, cy, r, a) {
  const pts = [[0,-r],[r*0.58,0],[0,r],[-r*0.58,0]];
  const c=Math.cos(a), s=Math.sin(a);
  pts.forEach(([px,py],i) => {
    const x=cx+px*c-py*s, y=cy+px*s+py*c;
    if(i===0) ctx.moveTo(x,y); else ctx.lineTo(x,y);
  });
  ctx.closePath();
}
function triPath(ctx, cx, cy, r, a) {
  for (let i = 0; i < 3; i++) {
    const th = (Math.PI*2/3)*i + a;
    if (i===0) ctx.moveTo(cx+r*Math.cos(th), cy+r*Math.sin(th));
    else        ctx.lineTo(cx+r*Math.cos(th), cy+r*Math.sin(th));
  }
  ctx.closePath();
}
function drawGrid(ctx, w, h, size, rgba) {
  ctx.beginPath();
  for (let x=size; x<w; x+=size) { ctx.moveTo(x,0); ctx.lineTo(x,h); }
  for (let y=size; y<h; y+=size) { ctx.moveTo(0,y); ctx.lineTo(w,y); }
  ctx.strokeStyle=rgba; ctx.lineWidth=0.5; ctx.stroke();
}
function easeOut(t) { const u = 1 - Math.min(Math.max(t, 0), 1); return 1 - u * u; }
function easeIn(t)  { const c = Math.min(Math.max(t, 0), 1); return c * c; }
function ltOp(born, elapsed, lifespan, fadeIn, fadeOut) {
  const age = elapsed - born;
  if (age < 0 || age >= lifespan) return 0;
  if (age < fadeIn)  return easeOut(age / fadeIn);
  if (age > lifespan - fadeOut) return easeOut((lifespan - age) / fadeOut);
  return 1;
}

// ── LiveCodeBlock — Camada 1 ──────────────────────────────────────────────────
// Um bloco = uma operação cognitiva com ciclo de vida completo.
// Estados: TYPING (digitação linha por linha) → ALIVE (visível, dim) → FADING.
class LiveCodeBlock {
  constructor(w, h, mob, def, orbX, orbY, orbR) {
    this.mob = mob;
    this.def = def;

    // Gera linhas (LOG usa timestamps reais de runtime)
    if (def.lines === null) {
      const now = new Date();
      const evts = [
        'memory retrieved', 'context built', 'ranking completed',
        'tool executed', 'response generated', 'streaming...',
      ];
      this.lines = evts.map((ev, i) => {
        const d  = new Date(now.getTime() + i * 1000);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `[${hh}:${mm}:${ss}] ${ev}`;
      });
    } else {
      this.lines = def.lines;
    }

    // Dimensões do painel
    const lh  = mob ? 11 : 13;
    const hh  = mob ? 15 : 18;
    const sh  = mob ? 11 : 13;
    const cw  = mob ? 5.4 : 6.3;
    const lnW = mob ? 16 : 20;
    const maxL = Math.max(
      ...this.lines.filter(Boolean).map(l => l.length),
      def.type.length + def.file.length + 3
    );
    this.lineH   = lh;
    this.hdrH    = hh;
    this.statusH = sh;
    this.tw = Math.min(Math.max(mob ? 165 : 210, Math.ceil(maxL * cw + lnW + 20)), mob ? 218 : 288);
    this.th = hh + 4 + this.lines.length * lh + sh + 6;
    this._lnW = lnW;

    // Posição: fora da zona de exclusão do Orb
    let bx = 8, by = 8;
    for (let att = 0; att < 40; att++) {
      let tx, ty;
      if (mob && att < 28) {
        // Mobile: preferir bandas superior/inferior (orb ocupa o centro vertical)
        ty = Math.random() < 0.5
          ? Math.floor(Math.random() * Math.max(8, orbY - orbR - this.th - 4))
          : Math.floor(orbY + orbR + Math.random() * Math.max(8, h - orbY - orbR - this.th - 4));
        ty = Math.max(4, Math.min(ty, h - this.th - 4));
        tx = Math.floor(Math.random() * Math.max(8, w - this.tw - 8)) + 4;
      } else {
        tx = Math.floor(Math.random() * Math.max(8, w - this.tw - 8)) + 4;
        ty = Math.floor(Math.random() * Math.max(8, h - this.th - 8)) + 4;
      }
      if (this._clear(tx, ty, orbX, orbY, orbR)) { bx = tx; by = ty; break; }
    }
    this.x = bx; this.y = by;

    // Estado
    this.state       = 'TYPING';
    this.born        = 0;
    this.completedAt = -1;
    this.fadeStartAt = -1;
    this.curLine     = 0;
    this.curChar     = 0;
    this.lineStartAt = 0;

    // Timings completamente independentes (sem sincronização entre blocos)
    this.charSpd   = 18 + Math.random() * 32;   // ms por caractere
    this.linePause = 65 + Math.random() * 115;   // pausa entre linhas
    this.aliveDur  = 3000 + Math.random() * 5500; // tempo visível após concluir
    this.fadeDur   = 1100 + Math.random() * 1200; // duração do fade out

    // Flutuação suave única por bloco (1-2px)
    this.fPhase = Math.random() * Math.PI * 2;
    this.fSpeed = 0.0004 + Math.random() * 0.0008;
    this.fAmp   = 0.7 + Math.random() * 1.4;
  }

  _clear(bx, by, ox, oy, or_) {
    // Teste de interseção retângulo × círculo
    const nearX = Math.max(bx, Math.min(ox, bx + this.tw));
    const nearY = Math.max(by, Math.min(oy, by + this.th));
    return (ox - nearX) ** 2 + (oy - nearY) ** 2 >= or_ * or_;
  }

  start(elapsed) {
    this.born = elapsed;
    this.lineStartAt = elapsed;
  }

  update(elapsed) {
    if (this.state === 'TYPING') {
      if (this.curLine >= this.lines.length) {
        this.state = 'ALIVE'; this.completedAt = elapsed; return;
      }
      const line    = this.lines[this.curLine];
      const lineAge = elapsed - this.lineStartAt;
      if (!line) {
        // Linha vazia: avança após breve pausa
        if (lineAge >= 80) { this.curLine++; this.lineStartAt = elapsed; }
        return;
      }
      this.curChar = Math.min(Math.floor(lineAge / this.charSpd), line.length);
      if (this.curChar >= line.length && lineAge >= line.length * this.charSpd + this.linePause) {
        this.curLine++; this.curChar = 0; this.lineStartAt = elapsed;
      }
    } else if (this.state === 'ALIVE') {
      if (elapsed - this.completedAt >= this.aliveDur) {
        this.state = 'FADING'; this.fadeStartAt = elapsed;
      }
    }
  }

  isDead(elapsed) {
    return this.state === 'FADING' && elapsed - this.fadeStartAt >= this.fadeDur;
  }

  startFading(elapsed) {
    if (this.state !== 'FADING') { this.state = 'FADING'; this.fadeStartAt = elapsed; }
  }

  draw(ctx, elapsed) {
    const age    = elapsed - this.born;
    const fadeIn = easeOut(Math.min(age / 420, 1));
    let opMult   = fadeIn;
    if (this.state === 'FADING') {
      opMult = easeIn(Math.max(0, 1 - (elapsed - this.fadeStartAt) / this.fadeDur)) * fadeIn;
    }
    if (opMult < 0.01) return;

    const floatY = Math.sin(elapsed * this.fSpeed + this.fPhase) * this.fAmp;
    const x = this.x, y = Math.round(this.y + floatY);
    const { tw, th, lineH, hdrH, statusH, _lnW, mob } = this;
    const amber = this.def.color === 'am';
    const done  = this.state !== 'TYPING';

    // TYPING: mais brilhante (0.48). ALIVE/FADING: mais suave (0.20).
    const op = (done ? 0.20 : 0.48) * opMult;

    const C  = a => amber ? `rgba(255,160,30,${a.toFixed(3)})` : `rgba(0,200,212,${a.toFixed(3)})`;
    const Cd = a => amber ? `rgba(255,160,30,${(a*0.48).toFixed(3)})` : `rgba(0,200,212,${(a*0.48).toFixed(3)})`;

    // Fundo do painel
    ctx.fillStyle = `rgba(1,4,18,${Math.min(op * 1.9, 0.92).toFixed(3)})`;
    ctx.fillRect(x, y, tw, th);

    // Borda principal
    ctx.strokeStyle = C(op * 0.88);
    ctx.lineWidth   = 0.75;
    ctx.strokeRect(x, y, tw, th);

    // Barra de acento no topo
    ctx.fillStyle = C(Math.min(op * 1.2, 0.80));
    ctx.fillRect(x, y, tw, 1.5);

    // Header: "TYPE · file.ext"
    ctx.font      = `bold ${mob ? 7 : 8}px monospace`;
    ctx.textAlign = 'left';
    ctx.fillStyle = C(Math.min(op * 1.35, 0.88));
    ctx.fillText(`${this.def.type} · ${this.def.file}`, x + 6, y + hdrH * 0.78);

    // Separador header
    ctx.beginPath();
    ctx.moveTo(x, y + hdrH); ctx.lineTo(x + tw, y + hdrH);
    ctx.strokeStyle = Cd(op); ctx.lineWidth = 0.4; ctx.stroke();

    // Linhas de código com numeração
    ctx.font = `${mob ? 6.5 : 7.5}px monospace`;
    for (let i = 0; i < this.lines.length; i++) {
      const ln = this.lines[i];
      const ly = y + hdrH + 3 + i * lineH + lineH * 0.78;

      // Durante TYPING: só exibe até linha atual (inclusive)
      if (this.state === 'TYPING') {
        if (i > this.curLine) break;
        if (i === this.curLine && this.curChar === 0 && ln) break;
      }
      if (!ln) continue; // linha vazia: manter espaço, não renderizar texto

      const isCur = (i === this.curLine && this.state === 'TYPING');

      // Número da linha (mais escuro)
      ctx.fillStyle = Cd(op);
      ctx.fillText(String(i + 1).padStart(2, '0'), x + 5, ly);

      // Conteúdo com cursor piscante na linha ativa
      const display = isCur ? ln.slice(0, this.curChar) + '▌' : ln;
      ctx.fillStyle = C(Math.min(isCur ? op * 1.25 : op * 0.88, 0.90));
      ctx.fillText(display, x + 5 + _lnW, ly);
    }

    // Separador antes do status
    const sepY = y + hdrH + 3 + this.lines.length * lineH + 2;
    ctx.beginPath();
    ctx.moveTo(x, sepY); ctx.lineTo(x + tw, sepY);
    ctx.strokeStyle = Cd(op); ctx.lineWidth = 0.3; ctx.stroke();

    // Linha de status
    ctx.font      = `${mob ? 6.5 : 7.5}px monospace`;
    ctx.fillStyle = done
      ? `rgba(52,210,85,${Math.min(op * 1.45, 0.90).toFixed(3)})`
      : C(op * 0.84);
    ctx.fillText(done ? this.def.done : this.def.run, x + 6, sepY + statusH * 0.78);
  }
}

// ── LiveCodeSystem — Camada 1 ─────────────────────────────────────────────────
// Gerencia até 5 blocos ativos. Sequência embaralhada → nunca repete padrão.
class LiveCodeSystem {
  constructor(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2;
    this.orbY = h * 0.46;
    this.orbR = mob ? 140 : 205;

    this.blocks       = [];
    this._maxBlocks   = 5;
    this._nextSpawnAt = 1000;
    this._seq         = this._shuffle(BLOCK_DEFS.map((_, i) => i));
    this._seqIdx      = 0;

    this._preseed();
  }

  _shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  _nextDef() {
    if (this._seqIdx >= this._seq.length) {
      this._seq = this._shuffle(BLOCK_DEFS.map((_, i) => i));
      this._seqIdx = 0;
    }
    return BLOCK_DEFS[this._seq[this._seqIdx++]];
  }

  _makeBlock(elapsed) {
    return new LiveCodeBlock(this.w, this.h, this.mob, this._nextDef(), this.orbX, this.orbY, this.orbR);
  }

  _preseed() {
    // Pré-semeia 2-3 blocos já em andamento para evitar tela vazia no início
    const count = this.mob ? 2 : 3;
    for (let i = 0; i < count; i++) {
      const block  = this._makeBlock(0);
      const preAge = 1200 + i * 2000 + Math.random() * 900;
      block.start(-preAge);
      // Fast-forward: simula updates para chegar ao estado correto em elapsed=0
      for (let t = 100; t <= preAge; t += 100) block.update(t - preAge);
      this.blocks.push(block);
    }
  }

  update(elapsed) {
    for (const b of this.blocks) b.update(elapsed);
    this.blocks = this.blocks.filter(b => !b.isDead(elapsed));

    const alive = this.blocks.filter(b => b.state !== 'FADING').length;
    if (alive < this._maxBlocks && elapsed >= this._nextSpawnAt) {
      const block = this._makeBlock(elapsed);
      block.start(elapsed);
      this.blocks.push(block);

      // Se ultrapassar o máximo, inicia fade do mais antigo
      const nonFading = this.blocks.filter(b => b.state !== 'FADING');
      if (nonFading.length > this._maxBlocks) {
        nonFading.sort((a, b_) => a.born - b_.born)[0].startFading(elapsed);
      }

      this._nextSpawnAt = elapsed + 1500 + Math.random() * 2500;
    }
  }

  draw(ctx, elapsed) {
    for (const b of this.blocks) b.draw(ctx, elapsed);
  }

  resize(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2;
    this.orbY = h * 0.46;
    this.orbR = mob ? 140 : 205;
    this.blocks = [];
    this._nextSpawnAt = 500;
    this._preseed();
  }
}

// ── Janelas Cognitivas — Camada 2 ─────────────────────────────────────────────
// Fragmentos temporários do pensamento: materializam com glow → borda desenhada
// → conteúdo em fases → desaparecimento. Máx. 2 simultâneas.

const COGWIN_DEFS = [
  { module: 'MEMORY SEARCH', sub: 'athena.memory', size: 'M', color: 'c',
    phases: [
      { msg: 'connecting to store...', dur: 800 },
      { msg: 'scanning 847 entries',   dur: 1200 },
      { msg: 'cosine pass: 0.72+',    dur: 900 },
      { msg: '12 candidates found',   dur: 700 },
      { msg: 'top-5 selected ✓', dur: 600, done: true },
    ],
  },
  { module: 'REASONING', sub: 'athena.chain', size: 'L', color: 'c',
    phases: [
      { msg: 'decomposing goal...',  dur: 700 },
      { msg: 'step 1/4: gather ctx', dur: 1100 },
      { msg: 'step 2/4: hypothesis', dur: 1300 },
      { msg: 'step 3/4: validate',   dur: 1000 },
      { msg: 'step 4/4: synthesize', dur: 800 },
      { msg: 'chain complete ✓', dur: 500, done: true },
    ],
  },
  { module: 'VECTOR SEARCH', sub: 'athena.embed', size: 'S', color: 'c',
    phases: [
      { msg: 'encoding query...', dur: 700 },
      { msg: 'k=10, dim=1536',    dur: 600 },
      { msg: 'top score: 0.91',   dur: 800 },
      { msg: 'reranked ✓',   dur: 500, done: true },
    ],
  },
  { module: 'PLANNER', sub: 'athena.plan', size: 'M', color: 'c',
    phases: [
      { msg: 'parsing intent...',  dur: 600 },
      { msg: 'task decomposition', dur: 1000 },
      { msg: '4 subtasks found',   dur: 700 },
      { msg: 'resolving deps...',  dur: 900 },
      { msg: 'schedule ready ✓', dur: 500, done: true },
    ],
  },
  { module: 'TOOL ROUTER', sub: 'athena.tools', size: 'S', color: 'am',
    phases: [
      { msg: 'selecting tool...',   dur: 600 },
      { msg: 'target: browser',     dur: 700 },
      { msg: 'executing call...',   dur: 1400 },
      { msg: 'result received ✓', dur: 500, done: true },
    ],
  },
  { module: 'GRAPH', sub: 'athena.graph', size: 'M', color: 'c',
    phases: [
      { msg: 'loading knowledge...', dur: 800 },
      { msg: '12 nodes traversed',   dur: 1100 },
      { msg: 'edge weight: 0.74',    dur: 700 },
      { msg: 'centrality done ✓', dur: 600, done: true },
    ],
  },
  { module: 'BROWSER', sub: 'tool.web', size: 'L', color: 'am',
    phases: [
      { msg: 'headless launch...',  dur: 700 },
      { msg: 'navigating target',   dur: 1000 },
      { msg: 'extracting content',  dur: 1500 },
      { msg: 'parsing HTML...',     dur: 900 },
      { msg: 'content ready ✓', dur: 500, done: true },
    ],
  },
  { module: 'EMBEDDINGS', sub: 'athena.embed', size: 'S', color: 'c',
    phases: [
      { msg: 'tokenizing input...', dur: 700 },
      { msg: 'model: text-emb-3',   dur: 500 },
      { msg: 'dim=1536 ready ✓', dur: 700, done: true },
    ],
  },
  { module: 'CONTEXT BUILD', sub: 'athena.ctx', size: 'L', color: 'c',
    phases: [
      { msg: 'assembling window...', dur: 800 },
      { msg: 'memory: 8 refs',       dur: 900 },
      { msg: 'docs: 5 chunks',       dur: 900 },
      { msg: 'history: 4 turns',     dur: 700 },
      { msg: '12.4k tokens ready ✓', dur: 600, done: true },
    ],
  },
  { module: 'VISION', sub: 'athena.vision', size: 'M', color: 'c',
    phases: [
      { msg: 'image loaded...',   dur: 600 },
      { msg: 'object detection',  dur: 1200 },
      { msg: '3 elements found',  dur: 700 },
      { msg: 'caption ready ✓', dur: 900, done: true },
    ],
  },
];

class CognitiveWindow {
  constructor(w, h, mob, def, orbX, orbY, orbR) {
    this.def = def; this.mob = mob;

    const twD = { S: 175, M: 238, L: 295 };
    const twM = { S: 145, M: 185, L: 220 };
    this.tw = (mob ? twM : twD)[def.size] || 238;

    const lh  = mob ? 11 : 13;
    const hH  = mob ? 16 : 20;
    const sH  = mob ? 11 : 13;
    const pad = mob ?  5 :  6;
    this.lineH = lh; this.hdrH = hH; this.subH = sH; this.padH = pad;

    const nP = def.phases.length;
    this.th = hH + sH + 4 + pad + nP * lh + pad + 3 + 5 + lh + 4;

    // Posição válida fora do Orbe
    let bx = 10, by = 10;
    for (let att = 0; att < 40; att++) {
      let tx, ty;
      if (mob && att < 28) {
        ty = Math.random() < 0.5
          ? Math.floor(Math.random() * Math.max(8, orbY - orbR - this.th - 4))
          : Math.floor(orbY + orbR + Math.random() * Math.max(8, h - orbY - orbR - this.th - 4));
        ty = Math.max(4, Math.min(ty, h - this.th - 4));
        tx = Math.floor(Math.random() * Math.max(8, w - this.tw - 8)) + 4;
      } else {
        tx = Math.floor(Math.random() * Math.max(8, w - this.tw - 8)) + 4;
        ty = Math.floor(Math.random() * Math.max(8, h - this.th - 8)) + 4;
      }
      if (this._clearOrb(tx, ty, orbX, orbY, orbR)) { bx = tx; by = ty; break; }
    }
    this.x = bx; this.y = by;

    // Máquina de estados
    this.state       = 'MATERIALIZING';
    this.born        = 0;
    this.completedAt = -1;
    this.fadeStartAt = -1;
    this.aliveDur    = 2500 + Math.random() * 3500;
    this.fadeDur     = 900  + Math.random() * 700;

    // Fases
    this.phases        = def.phases;
    this.phasesDone    = [];
    this.curPhaseIdx   = -1;
    this.curPhaseChar  = 0;
    this.curPhaseStart = -1;
    this.charSpd       = 18 + Math.random() * 16;

    // Flutuação
    this.fPhase = Math.random() * Math.PI * 2;
    this.fSpd   = 0.00030 + Math.random() * 0.00050;
    this.fAmp   = 0.6 + Math.random() * 1.2;
  }

  _clearOrb(bx, by, ox, oy, or_) {
    const nx = Math.max(bx, Math.min(ox, bx + this.tw));
    const ny = Math.max(by, Math.min(oy, by + this.th));
    return (ox - nx) ** 2 + (oy - ny) ** 2 >= or_ * or_;
  }

  start(elapsed) { this.born = elapsed; this.curPhaseIdx = -1; }

  update(elapsed) {
    const age = elapsed - this.born;
    if (this.state === 'MATERIALIZING' && age >= 350) this.state = 'DRAWING';
    if (this.state === 'DRAWING' && age >= 850) {
      this.state = 'REVEALING';
      this.curPhaseIdx  = 0;
      this.curPhaseChar = 0;
      this.curPhaseStart = elapsed;
    }
    if (this.state === 'REVEALING' || this.state === 'ACTIVE') this._tick(elapsed);
    if (this.state === 'ACTIVE' && elapsed - this.completedAt >= this.aliveDur) {
      this.state = 'FADING'; this.fadeStartAt = elapsed;
    }
  }

  _tick(elapsed) {
    if (this.curPhaseIdx >= this.phases.length) {
      if (this.state !== 'ACTIVE') { this.state = 'ACTIVE'; this.completedAt = elapsed; }
      return;
    }
    const ph      = this.phases[this.curPhaseIdx];
    const lineAge = elapsed - this.curPhaseStart;
    this.curPhaseChar = Math.min(Math.floor(lineAge / this.charSpd), ph.msg.length);
    if (this.curPhaseChar >= ph.msg.length && lineAge >= ph.msg.length * this.charSpd + ph.dur) {
      this.phasesDone.push(ph.msg);
      this.curPhaseIdx++;
      if (this.curPhaseIdx < this.phases.length) {
        this.curPhaseChar  = 0;
        this.curPhaseStart = elapsed;
      }
    }
  }

  isDead(elapsed) { return this.state === 'FADING' && elapsed - this.fadeStartAt >= this.fadeDur; }

  draw(ctx, elapsed) {
    const age = elapsed - this.born;
    let opMult = easeOut(Math.min(age / 350, 1.0));
    if (this.state === 'FADING')
      opMult = easeIn(Math.max(0, 1 - (elapsed - this.fadeStartAt) / this.fadeDur));
    if (opMult < 0.01) return;

    const floatY = Math.sin(elapsed * this.fSpd + this.fPhase) * this.fAmp;
    const x = this.x, y = Math.round(this.y + floatY);
    const { tw, th, hdrH, subH, lineH, padH, mob } = this;
    const am = this.def.color === 'am';
    const C  = a => am ? `rgba(255,160,30,${a.toFixed(3)})` : `rgba(0,200,212,${a.toFixed(3)})`;
    const Cd = a => am ? `rgba(255,160,30,${(a*0.42).toFixed(3)})` : `rgba(0,200,212,${(a*0.42).toFixed(3)})`;

    // ── Glow de materialização ─────────────────────────────────────────────────
    if (this.state === 'MATERIALIZING' || this.state === 'DRAWING') {
      const mProg = easeOut(Math.min(age / 350, 1));
      const dProg = this.state === 'DRAWING' ? easeOut(Math.min((age - 350) / 500, 1)) : 0;
      const gOp   = (1 - dProg) * 0.65 * mProg * opMult;
      if (gOp > 0.01) {
        const gcx = x + tw * 0.5, gcy = y + th * 0.5;
        const gr  = 6 + mProg * 26;
        const grd = ctx.createRadialGradient(gcx, gcy, 0, gcx, gcy, gr);
        grd.addColorStop(0, C(gOp)); grd.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grd; ctx.fillRect(x, y, tw, th);
      }
    }

    // ── Painel + borda desenhada progressivamente ──────────────────────────────
    if (this.state !== 'MATERIALIZING') {
      const dProg = this.state === 'DRAWING' ? easeOut(Math.min((age - 350) / 500, 1)) : 1;
      const pOp   = dProg * opMult;

      ctx.fillStyle = `rgba(1,4,18,${Math.min(pOp * 0.86, 0.86).toFixed(3)})`;
      ctx.fillRect(x, y, tw, th);

      // Barra de acento topo
      ctx.fillStyle = C(pOp * 0.50 * Math.min(dProg * 2, 1));
      ctx.fillRect(x, y, tw, mob ? 1.5 : 2);

      // Borda clockwise
      const bOp = (this.state === 'REVEALING' ? 0.50 : 0.22) * pOp;
      this._border(ctx, x, y, tw, th, dProg, C(bOp), 0.75);
    }

    // ── Conteúdo (fases) ───────────────────────────────────────────────────────
    if (this.state === 'REVEALING' || this.state === 'ACTIVE' || this.state === 'FADING') {
      const done = this.state !== 'REVEALING';
      const cOp  = (done ? 0.22 : 0.48) * opMult;

      ctx.font = `bold ${mob ? 7 : 8}px monospace`; ctx.textAlign = 'left';
      ctx.fillStyle = C(Math.min(cOp * 1.4, 0.92));
      ctx.fillText(this.def.module, x + 7, y + hdrH * 0.78);

      ctx.font = `${mob ? 6 : 7}px monospace`;
      ctx.fillStyle = Cd(cOp);
      ctx.fillText(this.def.sub, x + 7, y + hdrH + subH * 0.82);

      const sep1Y = y + hdrH + subH + 3;
      ctx.beginPath(); ctx.moveTo(x + 4, sep1Y); ctx.lineTo(x + tw - 4, sep1Y);
      ctx.strokeStyle = Cd(cOp); ctx.lineWidth = 0.35; ctx.stroke();

      ctx.font = `${mob ? 6.5 : 7.5}px monospace`;
      const logY0 = sep1Y + padH;
      const nP    = this.phases.length;

      for (let i = 0; i < nP; i++) {
        const ph = this.phases[i];
        const ly = logY0 + i * lineH + lineH * 0.80;
        if (i < this.phasesDone.length) {
          ctx.fillStyle = ph.done
            ? `rgba(52,210,85,${Math.min(cOp * 1.25, 0.88).toFixed(3)})`
            : C(cOp * 0.80);
          ctx.fillText(`> ${ph.msg}`, x + 7, ly);
        } else if (i === this.curPhaseIdx && this.curPhaseIdx >= 0) {
          ctx.fillStyle = C(Math.min(cOp * 1.25, 0.90));
          ctx.fillText(`> ${ph.msg.slice(0, this.curPhaseChar)}▌`, x + 7, ly);
        }
      }

      // Barra de progresso
      const barY = logY0 + nP * lineH + 2;
      const pct  = done ? 1.0
        : (this.curPhaseIdx >= 0 && this.curPhaseIdx < nP)
          ? Math.min((this.phasesDone.length + this.curPhaseChar / Math.max(1, this.phases[this.curPhaseIdx].msg.length)) / nP, 0.97)
          : 0;
      ctx.fillStyle = `rgba(255,255,255,${(cOp * 0.07).toFixed(3)})`;
      ctx.fillRect(x + 6, barY, tw - 12, 3);
      ctx.fillStyle = pct >= 0.99
        ? `rgba(52,210,85,${Math.min(cOp * 0.95, 0.85).toFixed(3)})`
        : C(cOp * 0.65);
      ctx.fillRect(x + 6, barY, Math.floor((tw - 12) * pct), 3);

      const sep2Y = barY + 6;
      ctx.beginPath(); ctx.moveTo(x + 4, sep2Y); ctx.lineTo(x + tw - 4, sep2Y);
      ctx.strokeStyle = Cd(cOp); ctx.lineWidth = 0.3; ctx.stroke();

      ctx.font = `${mob ? 6 : 7}px monospace`;
      let stTxt, stCol;
      if (done) {
        stTxt = 'completed ✓';
        stCol = `rgba(52,210,85,${Math.min(cOp * 1.2, 0.85).toFixed(3)})`;
      } else if (this.curPhaseIdx >= 0) {
        stTxt = `running · ${this.curPhaseIdx + 1}/${nP}`;
        stCol = C(cOp * 0.80);
      } else {
        stTxt = 'initializing...'; stCol = Cd(cOp);
      }
      ctx.fillStyle = stCol;
      ctx.fillText(stTxt, x + 7, sep2Y + lineH * 0.82);
    }
  }

  _border(ctx, x, y, tw, th, progress, color, lw) {
    if (progress <= 0) return;
    const perim = 2 * (tw + th);
    let rem = perim * Math.min(progress, 1);
    ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.beginPath();
    if (rem > 0) { const s = Math.min(rem, tw); ctx.moveTo(x,    y);    ctx.lineTo(x + s,    y);        rem -= s; }
    if (rem > 0) { const s = Math.min(rem, th); ctx.moveTo(x+tw, y);    ctx.lineTo(x+tw,     y + s);    rem -= s; }
    if (rem > 0) { const s = Math.min(rem, tw); ctx.moveTo(x+tw, y+th); ctx.lineTo(x+tw - s, y+th);     rem -= s; }
    if (rem > 0) { const s = Math.min(rem, th); ctx.moveTo(x,    y+th); ctx.lineTo(x,        y+th - s);           }
    ctx.stroke();
  }
}

class CogWinSystem {
  constructor(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this.orbR = mob ? 150 : 215;
    this.wins    = [];
    this._nextAt = 3800 + Math.random() * 2000;
    this._seq    = this._shuffle(COGWIN_DEFS.map((_, i) => i));
    this._seqIdx = 0;
  }

  _shuffle(a) {
    const b = [...a];
    for (let i = b.length - 1; i > 0; i--) { const j = 0|Math.random()*(i+1); [b[i],b[j]]=[b[j],b[i]]; }
    return b;
  }

  _nextDef() {
    if (this._seqIdx >= this._seq.length) { this._seq = this._shuffle(COGWIN_DEFS.map((_, i) => i)); this._seqIdx = 0; }
    return COGWIN_DEFS[this._seq[this._seqIdx++]];
  }

  update(elapsed) {
    for (const win of this.wins) win.update(elapsed);
    this.wins = this.wins.filter(win => !win.isDead(elapsed));
    const alive = this.wins.filter(win => win.state !== 'FADING').length;
    if (alive < 2 && elapsed >= this._nextAt) {
      const win = new CognitiveWindow(this.w, this.h, this.mob, this._nextDef(), this.orbX, this.orbY, this.orbR);
      win.start(elapsed);
      this.wins.push(win);
      this._nextAt = elapsed + 4500 + Math.random() * 5500;
    }
  }

  draw(ctx, elapsed) { for (const win of this.wins) win.draw(ctx, elapsed); }

  resize(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this.orbR = mob ? 150 : 215;
    this.wins = []; this._nextAt = 1500;
  }
}

// ── Fluxo Cognitivo — Camada 3 ────────────────────────────────────────────────
// Pulsos de informação que viajam em arcos Bezier curvos entre elementos ativos.
// Nascem de eventos reais (bloco completo, fase concluída) ou fill periódico.
// Max 6 simultâneos. Nunca dominam a cena — conectam sem poluir.

class CogPulse {
  constructor(sx, sy, dx, dy, cx, cy, color, pixPerMs) {
    this.sx = sx; this.sy = sy;
    this.dx = dx; this.dy = dy;
    this.cx = cx; this.cy = cy;
    this.color = color;
    this.progress = 0;
    this.arrived  = false;
    this._flashSpawned = false;

    const approxLen   = Math.max(Math.hypot(dx - sx, dy - sy) * 1.1, 80);
    this.speedPerMs   = pixPerMs / approxLen;

    this.pauseAt    = Math.random() < 0.28 ? 0.22 + Math.random() * 0.46 : -1;
    this.pauseDur   = 160 + Math.random() * 320;
    this.pauseStart = -1;
  }

  _b(t) {
    const u = 1 - t;
    return {
      x: u*u*this.sx + 2*u*t*this.cx + t*t*this.dx,
      y: u*u*this.sy + 2*u*t*this.cy + t*t*this.dy,
    };
  }

  update(elapsed, dt) {
    if (this.arrived) return;
    if (this.pauseAt > 0 && this.progress >= this.pauseAt && this.pauseStart < 0)
      this.pauseStart = elapsed;
    if (this.pauseStart > 0 && elapsed - this.pauseStart < this.pauseDur) return;
    this.progress = Math.min(this.progress + this.speedPerMs * dt, 1);
    if (this.progress >= 1) this.arrived = true;
  }

  draw(ctx, mob) {
    const t = this.progress;
    if (t <= 0.008) return;

    const am = this.color === 'am';
    const [r, g, b] = am ? [255, 160, 30] : [0, 200, 212];
    const tailLen = 0.18;
    const tailT   = Math.max(0, t - tailLen);
    const head    = this._b(t);
    const tail    = this._b(tailT);

    if (Math.hypot(head.x - tail.x, head.y - tail.y) < 0.5) return;

    // Streak alongado com gradiente cauda→cabeça
    const pts = [];
    const nSeg = 8;
    for (let i = 0; i <= nSeg; i++) pts.push(this._b(tailT + (t - tailT) * (i / nSeg)));

    const grd = ctx.createLinearGradient(tail.x, tail.y, head.x, head.y);
    grd.addColorStop(0,   `rgba(${r},${g},${b},0)`);
    grd.addColorStop(0.55, `rgba(${r},${g},${b},0.18)`);
    grd.addColorStop(1,   `rgba(${r},${g},${b},0.55)`);

    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.strokeStyle = grd;
    ctx.lineWidth   = mob ? 1.0 : 1.6;
    ctx.lineCap     = 'round';
    ctx.stroke();

    // Brilho na cabeça
    const hw  = mob ? 2.8 : 4.0;
    const hgd = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, hw);
    hgd.addColorStop(0, `rgba(${r},${g},${b},0.70)`);
    hgd.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = hgd;
    ctx.fillRect(head.x - hw, head.y - hw, hw * 2, hw * 2);
  }
}

class ArrivalFlash {
  constructor(x, y, color, elapsed) {
    this.x = x; this.y = y; this.color = color;
    this.born = elapsed;
    this.dur  = 340 + Math.random() * 180;
  }
  draw(ctx, elapsed) {
    const op = Math.max(0, 1 - (elapsed - this.born) / this.dur);
    if (op < 0.01) return;
    const am = this.color === 'am';
    const [r, g, b] = am ? [255, 160, 30] : [0, 200, 212];
    const rad = 3 + (1 - op) * 16;
    const grd = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, rad);
    grd.addColorStop(0, `rgba(${r},${g},${b},${(op * 0.40).toFixed(3)})`);
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grd;
    ctx.fillRect(this.x - rad, this.y - rad, rad * 2, rad * 2);
  }
  isDone(elapsed) { return elapsed - this.born >= this.dur; }
}

class CogPulseSystem {
  constructor(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this.pulses       = [];
    this.flashes      = [];
    this._lastBlk     = new Map();
    this._lastWin     = new Map();
    this._fillAt      = 2800 + Math.random() * 2000;
    this._prevElapsed = -1;
  }

  _ctrl(sx, sy, dx, dy) {
    const len = Math.max(Math.hypot(dx - sx, dy - sy), 1);
    const mx  = (sx + dx) * 0.5, my = (sy + dy) * 0.5;
    const nx  = -(dy - sy) / len, ny = (dx - sx) / len;
    const crv = (Math.random() - 0.5) * len * 0.42;
    return {
      cx: mx + nx * crv + (this.orbX - mx) * 0.07,
      cy: my + ny * crv + (this.orbY - my) * 0.07,
    };
  }

  _spawn(sx, sy, dx, dy, color) {
    if (this.pulses.filter(p => !p.arrived).length >= 6) return;
    const { cx, cy } = this._ctrl(sx, sy, dx, dy);
    const ppm = 0.13 + Math.random() * 0.13;
    this.pulses.push(new CogPulse(sx, sy, dx, dy, cx, cy, color, ppm));
  }

  _dests(blocks, wins) {
    const ds = [{ x: this.orbX, y: this.orbY, orb: true }];
    for (const b of blocks)
      if (b.state === 'TYPING' || b.state === 'ALIVE')
        ds.push({ x: b.x + b.tw * 0.5, y: b.y + b.th * 0.5 });
    for (const w of wins)
      if (w.state === 'REVEALING' || w.state === 'ACTIVE')
        ds.push({ x: w.x + w.tw * 0.5, y: w.y + w.th * 0.5 });
    return ds;
  }

  _pick(dests, sx, sy, preferOrb) {
    if (dests.length === 0) return { x: this.orbX, y: this.orbY };
    const orb = dests.find(d => d.orb);
    if (preferOrb || Math.random() < 0.58) return orb || dests[0];
    const others = dests.filter(d => !d.orb && Math.hypot(d.x - sx, d.y - sy) > 60);
    return others.length > 0 ? others[0|Math.random()*others.length] : (orb || dests[0]);
  }

  scan(blocks, wins, elapsed) {
    const dests = this._dests(blocks, wins);

    // Blocos: detecta TYPING→ALIVE (conclusão) e avanço de linha
    for (const b of blocks) {
      const prev = this._lastBlk.get(b) || {};
      if (prev.state === 'TYPING' && b.state === 'ALIVE') {
        const sx = b.x + b.tw * 0.5, sy = b.y + b.th * 0.5;
        const d  = this._pick(dests, sx, sy, true);
        this._spawn(sx, sy, d.x, d.y, 'am');
        if (Math.random() < 0.45) {
          const d2 = this._pick(dests, sx, sy, false);
          this._spawn(sx, sy, d2.x, d2.y, 'c');
        }
      } else if (b.state === 'TYPING' && prev.curLine !== undefined && b.curLine > prev.curLine && Math.random() < 0.32) {
        const d = this._pick(dests, b.x + b.tw*0.5, b.y + b.th*0.5, false);
        this._spawn(b.x + b.tw*0.5, b.y + b.th*0.5, d.x, d.y, 'c');
      }
      this._lastBlk.set(b, { state: b.state, curLine: b.curLine });
    }

    // Janelas: detecta conclusão de cada fase
    for (const w of wins) {
      const prev    = this._lastWin.get(w) || { phasesDone: 0 };
      const newDone = w.phasesDone.length - prev.phasesDone;
      if (newDone > 0) {
        const isDone = w.phases[w.phasesDone.length - 1]?.done === true;
        const sx = w.x + w.tw * 0.5, sy = w.y + w.th * 0.5;
        const d  = this._pick(dests, sx, sy, isDone);
        this._spawn(sx, sy, d.x, d.y, isDone ? 'am' : 'c');
        if (isDone && Math.random() < 0.50) {
          const d2 = this._pick(dests, sx, sy, false);
          this._spawn(sx, sy, d2.x, d2.y, 'c');
        }
      }
      this._lastWin.set(w, { phasesDone: w.phasesDone.length });
    }

    // Limpa entradas obsoletas
    const ab = new Set(blocks), aw = new Set(wins);
    for (const k of this._lastBlk.keys()) if (!ab.has(k)) this._lastBlk.delete(k);
    for (const k of this._lastWin.keys()) if (!aw.has(k)) this._lastWin.delete(k);

    // Fill periódico: manter mínimo de 3 ativos
    if (elapsed >= this._fillAt) {
      const alive = this.pulses.filter(p => !p.arrived).length;
      if (alive < 3) {
        const srcs = [];
        for (const b of blocks)
          if (b.state === 'TYPING' || b.state === 'ALIVE')
            srcs.push({ x: b.x + b.tw*0.5, y: b.y + b.th*0.5 });
        for (const w of wins)
          if (w.state === 'REVEALING')
            srcs.push({ x: w.x + w.tw*0.5, y: w.y + w.th*0.5 });
        if (srcs.length > 0) {
          const s = srcs[0|Math.random()*srcs.length];
          const d = this._pick(dests, s.x, s.y, Math.random() < 0.60);
          this._spawn(s.x, s.y, d.x, d.y, 'c');
        }
      }
      this._fillAt = elapsed + 1800 + Math.random() * 2400;
    }
  }

  update(elapsed) {
    const dt = this._prevElapsed >= 0 ? Math.min(elapsed - this._prevElapsed, 100) : 16;
    this._prevElapsed = elapsed;
    for (const p of this.pulses) {
      p.update(elapsed, dt);
      if (p.arrived && !p._flashSpawned) {
        this.flashes.push(new ArrivalFlash(p.dx, p.dy, p.color, elapsed));
        p._flashSpawned = true;
      }
    }
    this.pulses  = this.pulses.filter(p => !p._flashSpawned);
    this.flashes = this.flashes.filter(f => !f.isDone(elapsed));
  }

  draw(ctx, elapsed) {
    ctx.save();
    for (const p of this.pulses) p.draw(ctx, this.mob);
    for (const f of this.flashes) f.draw(ctx, elapsed);
    ctx.restore();
  }

  resize(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this.pulses = []; this.flashes = [];
    this._fillAt = 1500;
  }
}

// ── Estrutura Cognitiva — Camada 4 ────────────────────────────────────────────
// Arquitetura geométrica que sustenta a cognição. Formas em planos de profundidade,
// progressivamente desenhadas, com respiração e rotação ultra-suave.
// Reage à intensidade de atividade dos módulos ativos (6–12 formas).

const STRUCT_TYPES = ['hex', 'dia', 'circ', 'tri', 'cross'];

class StructShape {
  constructor(w, h, mob, orbX, orbY, orbR, depth) {
    this.depth = depth; this.mob = mob;
    this.type  = STRUCT_TYPES[0|Math.random()*STRUCT_TYPES.length];

    const szD = [22, 36, 52], szM = [14, 22, 32];
    this.size  = (mob ? szM : szD)[depth] * (0.65 + Math.random() * 0.70);
    this.maxOp = [0.035, 0.072, 0.135][depth];

    // Posição excluindo zona do Orbe
    let bx = 20, by = 20;
    for (let att = 0; att < 40; att++) {
      const tx = this.size + Math.random() * Math.max(4, w - this.size * 2);
      const ty = this.size + Math.random() * Math.max(4, h - this.size * 2);
      if (Math.hypot(orbX - tx, orbY - ty) >= orbR + this.size + 12) { bx = tx; by = ty; break; }
    }
    this.x = bx; this.y = by;

    this.rot   = Math.random() * Math.PI * 2;
    this.rotSp = (Math.random() - 0.5) * 0.00007 * (0.8 + depth * 0.55);

    this.scPh  = Math.random() * Math.PI * 2;
    this.scSp  = 0.00038 + Math.random() * 0.00055;
    this.scAmp = 0.016 + Math.random() * 0.028;

    this.fPh   = Math.random() * Math.PI * 2;
    this.fSp   = 0.00022 + Math.random() * 0.00038;
    this.fAmp  = 0.7 + Math.random() * 1.8;

    this.born     = 0;
    this.drawDur  = 800  + Math.random() * 1200;
    this.aliveDur = 3500 + Math.random() * 6500;
    this.fadeDur  = 600  + Math.random() * 900;
    this.state    = 'DRAWING';
    this.fadeAt   = -1;
  }

  start(elapsed) { this.born = elapsed; }

  update(elapsed) {
    const age = elapsed - this.born;
    if (this.state === 'DRAWING' && age >= this.drawDur) this.state = 'ALIVE';
    if (this.state === 'ALIVE' && age >= this.drawDur + this.aliveDur) {
      this.state = 'FADING'; this.fadeAt = elapsed;
    }
  }

  isDead(elapsed) { return this.state === 'FADING' && elapsed - this.fadeAt >= this.fadeDur; }
  startFade(elapsed) { if (this.state !== 'FADING') { this.state = 'FADING'; this.fadeAt = elapsed; } }

  draw(ctx, elapsed) {
    const age = elapsed - this.born;
    let op;
    if      (this.state === 'DRAWING') op = Math.min(age / this.drawDur, 1) * this.maxOp;
    else if (this.state === 'ALIVE')   op = this.maxOp;
    else                               op = Math.max(0, 1 - (elapsed - this.fadeAt) / this.fadeDur) * this.maxOp;
    if (op < 0.004) return;

    const drawProg = this.state === 'DRAWING' ? Math.min(age / this.drawDur, 1) : 1;
    const rot      = this.rot + elapsed * this.rotSp;
    const scale    = 1 + Math.sin(elapsed * this.scSp + this.scPh) * this.scAmp;
    const floatY   = Math.sin(elapsed * this.fSp + this.fPh) * this.fAmp;
    const sz       = this.size * scale;
    const col      = `rgba(0,200,212,${op.toFixed(3)})`;
    const lw       = this.mob ? 0.45 : 0.65;

    ctx.save();
    ctx.translate(this.x, Math.round(this.y + floatY));
    ctx.rotate(rot);
    ctx.strokeStyle = col;
    ctx.fillStyle   = col;
    ctx.lineWidth   = lw;

    switch (this.type) {
      case 'hex':   this._poly(ctx, 6, sz, drawProg); break;
      case 'dia':   this._poly(ctx, 4, sz, drawProg); break;
      case 'tri':   this._poly(ctx, 3, sz, drawProg); break;
      case 'cross': this._cross(ctx, sz, drawProg); break;
      case 'circ':  this._circ(ctx, sz, drawProg); break;
    }

    ctx.restore();
  }

  _poly(ctx, n, sz, progress) {
    const step = (Math.PI * 2) / n;
    const a0   = -Math.PI / 2;
    const v    = [];
    for (let i = 0; i <= n; i++) v.push({ x: Math.cos(a0 + i*step)*sz, y: Math.sin(a0 + i*step)*sz });
    const sl  = Math.hypot(v[1].x - v[0].x, v[1].y - v[0].y);
    let rem   = sl * n * progress;

    ctx.beginPath();
    ctx.moveTo(v[0].x, v[0].y);
    for (let i = 0; i < n && rem > 0; i++) {
      const dx = v[i+1].x - v[i].x, dy = v[i+1].y - v[i].y;
      const t  = Math.min(rem / sl, 1);
      ctx.lineTo(v[i].x + dx*t, v[i].y + dy*t);
      rem -= sl;
    }
    ctx.stroke();
  }

  _cross(ctx, sz, progress) {
    const arm = sz * 0.65, gap = sz * 0.20;
    const pA  = Math.min(progress * 2, 1);
    const pC  = Math.min(progress * 5, 1);

    ctx.beginPath(); ctx.arc(0, 0, gap * 0.45 * pC, 0, Math.PI * 2); ctx.fill();

    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2, len = (arm - gap) * pA;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a)*gap, Math.sin(a)*gap);
      ctx.lineTo(Math.cos(a)*(gap+len), Math.sin(a)*(gap+len));
      ctx.stroke();
    }

    if (progress >= 0.85) {
      ctx.globalAlpha = (progress - 0.85) / 0.15;
      for (let i = 0; i < 4; i++) {
        const a = i * Math.PI / 2, pa = a + Math.PI / 2;
        const ax = Math.cos(a)*arm, ay = Math.sin(a)*arm;
        const px = Math.cos(pa)*gap*0.5, py = Math.sin(pa)*gap*0.5;
        ctx.beginPath(); ctx.moveTo(ax+px, ay+py); ctx.lineTo(ax-px, ay-py); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }

  _circ(ctx, sz, progress) {
    ctx.beginPath();
    ctx.arc(0, 0, sz, -Math.PI/2, -Math.PI/2 + Math.PI*2*progress);
    ctx.stroke();

    if (progress >= 0.5) {
      ctx.globalAlpha = (progress - 0.5) / 0.5;
      const iSz = sz * 0.60;
      ctx.beginPath();
      ctx.arc(0, 0, iSz, -Math.PI/2, -Math.PI/2 + Math.PI*2*((progress-0.5)/0.5));
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    if (progress >= 0.75) {
      ctx.globalAlpha = (progress - 0.75) / 0.25;
      const tLen = sz * 0.14;
      for (let i = 0; i < 4; i++) {
        const a = i * Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a)*(sz-tLen), Math.sin(a)*(sz-tLen));
        ctx.lineTo(Math.cos(a)*(sz+tLen*0.6), Math.sin(a)*(sz+tLen*0.6));
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }
}

class StructSystem {
  constructor(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX  = w / 2; this.orbY = h * 0.46;
    this.orbR  = mob ? 140 : 205;
    this.shapes  = [];
    this._nextAt = 400 + Math.random() * 600;
    this._target = 8;
  }

  _make(elapsed) {
    const depth = Math.floor(Math.random() * 3);
    const s = new StructShape(this.w, this.h, this.mob, this.orbX, this.orbY, this.orbR, depth);
    s.start(elapsed); return s;
  }

  update(elapsed, activityCount) {
    for (const s of this.shapes) s.update(elapsed);
    this.shapes = this.shapes.filter(s => !s.isDead(elapsed));

    this._target = this._rhythmTarget !== undefined ? this._rhythmTarget
                 : activityCount <= 1 ? 6
                 : activityCount <= 3 ? 8
                 : activityCount <= 5 ? 10 : 12;

    const alive = this.shapes.filter(s => s.state !== 'FADING').length;

    if (alive > this._target) {
      const cands = this.shapes.filter(s => s.state === 'ALIVE');
      if (cands.length > 0) cands[0].startFade(elapsed);
    }

    if (alive < this._target && elapsed >= this._nextAt) {
      this.shapes.push(this._make(elapsed));
      this._nextAt = elapsed + 500 + Math.random() * 1100;
    }
  }

  draw(ctx, elapsed) {
    const sorted = [...this.shapes].sort((a, b) => a.depth - b.depth);
    for (const s of sorted) s.draw(ctx, elapsed);
  }

  resize(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this.orbR = mob ? 140 : 205;
    this.shapes = []; this._nextAt = 400;
  }
}

// ── Luz, Atmosfera e Profundidade — Camada 5 ──────────────────────────────────
// Dois passes: pré (luz ambiente driftante) e pós (vinheta, influência do Orbe,
// perspectiva atmosférica, oclusão sutil). Não cria elementos — transforma.

class AtmosphereSystem {
  constructor(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this._prevElapsed = -1;
    this._nodes = this._mkNodes(w, h);
    this._vigR  = Math.max(w, h) * 0.68;
  }

  _mkNodes(w, h) {
    const rnd = (lo, hi) => lo + Math.random() * (hi - lo);
    const m   = this.mob;
    return [
      { x: w*0.15, y: h*0.22, vx: rnd(0.008,0.014), vy: rnd(0.004,0.009),
        r: w*(m?0.52:0.40), col:[0,200,212],  maxOp:0.012, ph:0              },
      { x: w*0.85, y: h*0.75, vx: rnd(-0.012,-0.007), vy: rnd(-0.008,-0.004),
        r: w*(m?0.46:0.36), col:[18,72,180],  maxOp:0.015, ph:Math.PI*0.71  },
      { x: w*0.22, y: h*0.82, vx: rnd(0.005,0.010), vy: rnd(-0.009,-0.005),
        r: w*(m?0.38:0.28), col:[64,148,255], maxOp:0.009, ph:Math.PI*1.43  },
      { x: w*0.78, y: h*0.18, vx: rnd(-0.009,-0.005), vy: rnd(0.006,0.011),
        r: w*(m?0.42:0.30), col:[0,130,195],  maxOp:0.010, ph:Math.PI*2.15  },
    ];
  }

  _updateNodes(elapsed) {
    if (this._prevElapsed === elapsed) return;
    const dt = this._prevElapsed >= 0 ? Math.min(elapsed - this._prevElapsed, 100) : 16;
    this._prevElapsed = elapsed;
    for (const n of this._nodes) {
      n.x += n.vx * dt; n.y += n.vy * dt;
      const mr = n.r * 0.3;
      if (n.x < -mr)       { n.x = -mr;       n.vx =  Math.abs(n.vx) * (0.85+Math.random()*0.30); }
      if (n.x > this.w+mr) { n.x = this.w+mr; n.vx = -Math.abs(n.vx) * (0.85+Math.random()*0.30); }
      if (n.y < -mr)       { n.y = -mr;       n.vy =  Math.abs(n.vy) * (0.85+Math.random()*0.30); }
      if (n.y > this.h+mr) { n.y = this.h+mr; n.vy = -Math.abs(n.vy) * (0.85+Math.random()*0.30); }
    }
  }

  drawPre(ctx, elapsed) {
    this._updateNodes(elapsed);
    for (const n of this._nodes) {
      const op  = n.maxOp * (0.65 + Math.sin(elapsed * 0.00038 + n.ph) * 0.35);
      const grd = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
      grd.addColorStop(0,   `rgba(${n.col[0]},${n.col[1]},${n.col[2]},${op.toFixed(4)})`);
      grd.addColorStop(0.55,`rgba(${n.col[0]},${n.col[1]},${n.col[2]},${(op*0.28).toFixed(4)})`);
      grd.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.fillRect(0, 0, this.w, this.h);
    }
  }

  drawPost(ctx, elapsed, actCt) {
    const { w, h, mob } = this;
    const cx = this.orbX, cy = this.orbY;

    // Presença do Orbe: irradiação extremamente discreta
    const oP  = 0.0062 + Math.sin(elapsed * 0.00063) * 0.0022;
    const og  = ctx.createRadialGradient(cx, cy, 0, cx, cy, mob ? w*0.78 : w*0.52);
    og.addColorStop(0,    `rgba(0,200,212,${oP.toFixed(4)})`);
    og.addColorStop(0.45, `rgba(0,80,160,${(oP*0.32).toFixed(4)})`);
    og.addColorStop(1,    'rgba(0,0,0,0)');
    ctx.fillStyle = og; ctx.fillRect(0, 0, w, h);

    // Perspectiva atmosférica: névoa nas bordas (planos distantes)
    const hz = ctx.createRadialGradient(cx, cy*0.92, 0, cx, cy*0.92, mob ? w*0.92 : w*0.72);
    hz.addColorStop(0,    'rgba(0,4,18,0)');
    hz.addColorStop(0.65, 'rgba(0,4,18,0)');
    hz.addColorStop(1,    'rgba(0,4,18,0.052)');
    ctx.fillStyle = hz; ctx.fillRect(0, 0, w, h);

    // Vinheta: enquadramento cinematográfico sutil
    const vg = ctx.createRadialGradient(cx, cy*0.94, 0, cx, cy*0.94, this._vigR);
    vg.addColorStop(0.28, 'rgba(0,0,0,0)');
    vg.addColorStop(1,    'rgba(0,0,8,0.13)');
    ctx.fillStyle = vg; ctx.fillRect(0, 0, w, h);

    // Oclusão: alta atividade → fundo perde levemente a intensidade
    if (actCt > 4) {
      ctx.fillStyle = `rgba(0,4,18,${Math.min((actCt-4)*0.007, 0.038).toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);
    }
  }

  resize(w, h, mob) {
    this.w = w; this.h = h; this.mob = mob;
    this.orbX = w / 2; this.orbY = h * 0.46;
    this._nodes = this._mkNodes(w, h);
    this._vigR  = Math.max(w, h) * 0.68;
  }
}

// ── Ritmo Cognitivo — Camada 6 ────────────────────────────────────────────────
// Orquestra o comportamento temporal de todas as camadas através de um ciclo de
// fases não-repetitivo. Nenhum elemento visual é criado — apenas timings são
// modulados. Quatro fases: idle → building → active → [intense] → convergence.

class CogRhythm {
  constructor() {
    this.intensity      = 0.28;
    this._targetInt     = 0.44;
    this._phase         = 'building';
    // Iniciar no meio de uma fase "building" para ter atividade imediata
    this._phaseEndAt    = 7000 + Math.random() * 6000;
    // Frequências incommensuráveis — sem LCM perceptível
    this._nA = Math.random() * 3000;
    this._nB = Math.random() * 3000;
    this._wasConverging = false;
  }

  _phaseDur(ph) {
    const r = { idle:[9000,22000], building:[5000,13000], active:[14000,28000],
                intense:[7000,16000], convergence:[4000,9000] };
    const [lo,hi] = r[ph] || [8000,15000];
    return lo + Math.random() * (hi - lo);
  }

  _nextPhase() {
    if (this._phase === 'idle')        return 'building';
    if (this._phase === 'building')    return 'active';
    if (this._phase === 'active')      return Math.random() < 0.62 ? 'intense' : 'convergence';
    if (this._phase === 'intense')     return 'convergence';
    return Math.random() < 0.72 ? 'idle' : 'building'; // convergence
  }

  _tgtForPhase(ph) {
    return { idle:0.18, building:0.44, active:0.66, intense:0.88, convergence:0.04 }[ph] || 0.30;
  }

  get level() {
    if (this.intensity < 0.18) return 'convergence';
    if (this.intensity < 0.44) return 'low';
    if (this.intensity < 0.68) return 'medium';
    return 'high';
  }

  update(elapsed) {
    if (elapsed >= this._phaseEndAt) {
      this._phase      = this._nextPhase();
      this._phaseEndAt = elapsed + this._phaseDur(this._phase);
      this._targetInt  = this._tgtForPhase(this._phase);
    }
    // Quatro osciladores assíncronos → padrão não-periódico
    const noise = 0.072 * Math.sin(elapsed * 0.000127 + this._nA)
                + 0.048 * Math.sin(elapsed * 0.000311 + this._nA * 0.73)
                + 0.036 * Math.sin(elapsed * 0.000089 + this._nB)
                + 0.026 * Math.sin(elapsed * 0.000557 + this._nB * 1.41);
    // _extTarget (CogOrchestrator) sobrescreve alvo interno quando definido
    const baseTarget = this._extTarget !== undefined ? this._extTarget : this._targetInt;
    const target = Math.max(0, Math.min(1, baseTarget + noise));
    this.intensity = this.intensity + (target - this.intensity) * 0.0014;
  }

  applyTo(structSys, winSys, pulseSys, elapsed) {
    const lvl = this.level;

    // ── StructSystem: número de formas por fase ────────────────────────────
    // _rhythmTarget = undefined → usa cálculo interno baseado em atividade
    structSys._rhythmTarget = (lvl === 'convergence') ? 2
                            : (lvl === 'low')         ? 5
                            : undefined;

    if (lvl === 'convergence') {
      for (const s of structSys.shapes)
        if (s.state === 'ALIVE' && Math.random() < 0.0025) s.startFade(elapsed);
    }

    // ── CogWinSystem: spawn e ciclo de vida das janelas ────────────────────
    if (lvl === 'convergence') {
      winSys._nextAt = elapsed + 45000;
      for (const w of winSys.wins)
        if (w.state === 'ACTIVE' && Math.random() < 0.0018) w.aliveDur = 0;
    } else if (this._wasConverging) {
      winSys._nextAt = Math.min(winSys._nextAt, elapsed + 2500);
    } else if (lvl === 'high' && winSys._nextAt > elapsed + 12000) {
      winSys._nextAt = elapsed + 3000 + Math.random() * 3000;
    }

    // ── CogPulseSystem: densidade de pulsos ───────────────────────────────
    if (lvl === 'convergence') {
      pulseSys._fillAt = elapsed + 50000;
    } else if (this._wasConverging) {
      pulseSys._fillAt = Math.min(pulseSys._fillAt, elapsed + 1500);
    } else if (lvl === 'low' && pulseSys._fillAt < elapsed + 6000) {
      pulseSys._fillAt = elapsed + 3500 + Math.random() * 2500;
    }

    this._wasConverging = (lvl === 'convergence');
  }
}

// ── Orquestração Cognitiva — Camada 8 ────────────────────────────────────────
// Transforma as 7 camadas em um único organismo cognitivo respondendo a sinais
// externos via window._athenaCogSignal(state, complexity).
// Estados: 'idle' | 'processing' | 'speaking'
// Regra cardinal: zero elementos visuais novos — coordenação de timing apenas.

class CogOrchestrator {
  constructor() {
    this._state     = 'idle';
    this._complex   = 0.5;
    this._stateAt   = -1;
    this._lastFade  = -9999;
    this._lastSweep = -9999;
  }

  signal(state, complexity = 0.5) {
    if (this._state === state) return;
    this._state     = state;
    this._complex   = Math.max(0, Math.min(1, complexity));
    this._stateAt   = -1;
    this._lastFade  = -9999;
    this._lastSweep = -9999;
  }

  update(elapsed, codeSystem, cogWinSys, cogPulseSys, structSys, rhythm) {
    if (this._stateAt < 0) this._stateAt = elapsed;
    const age = elapsed - this._stateAt;
    if      (this._state === 'processing') this._doProcessing(elapsed, age, codeSystem, cogWinSys, cogPulseSys, structSys, rhythm);
    else if (this._state === 'speaking')   this._doSpeaking(elapsed, age, codeSystem, cogWinSys, cogPulseSys, structSys, rhythm);
    else                                   this._doIdle(structSys, rhythm);
  }

  // Linha do tempo cognitiva — estágios 1-8.
  // Complexidade (0-1) escala: velocidade de spawn, densidade e pico de intensidade.
  _doProcessing(elapsed, age, cs, ws, ps, ss, rh) {
    const c = this._complex;

    if (age < 800) {
      // Estágio 1-2: despertar — ritmo emerge suavemente do estado passivo
      rh._extTarget = 0.22 + 0.30 * easeOut(age / 800);

    } else if (age < 2500) {
      // Estágio 3: Códigos Vivos surgem em cascata
      rh._extTarget = 0.52 + c * 0.22 * easeOut((age - 800) / 1700);
      const gap = Math.max(400, 1000 - c * 500);
      if (cs._nextSpawnAt > elapsed + gap) cs._nextSpawnAt = elapsed + gap * (0.4 + Math.random() * 0.6);

    } else if (age < 4000) {
      // Estágio 4: Janelas Cognitivas ativam
      rh._extTarget = 0.52 + c * 0.22;
      if (ws._nextAt > elapsed + 1200) ws._nextAt = elapsed + 400 + Math.random() * 800;

    } else if (age < 5500) {
      // Estágio 5: Fluxo Cognitivo intensifica
      rh._extTarget = 0.52 + c * 0.22;
      if (ps._fillAt > elapsed + 1000) ps._fillAt = elapsed + 500 + Math.random() * 500;

    } else {
      // Estágio 6-7: pico sustentado — CogRhythm livre; Estrutura fortalecida
      rh._extTarget    = undefined;
      ss._rhythmTarget = Math.round(7 + Math.round(c * 5)); // 7-12 formas por complexidade
    }
  }

  // Estágio 9: convergência progressiva — cena desacelera organicamente para o Orbe.
  _doSpeaking(elapsed, age, cs, ws, ps, ss, rh) {
    const converge = easeOut(Math.min(age / 7500, 1));

    // Ritmo desce em direção ao quase-silêncio
    rh._extTarget = 0.62 * (1 - converge) + 0.04 * converge;

    // Estrutura se dissolve progressivamente
    ss._rhythmTarget = Math.max(2, Math.round(8 - converge * 6));

    // Novas janelas bloqueadas — narrativa converge para o Orbe
    ws._nextAt = Math.max(ws._nextAt, elapsed + 28000);

    // Encurta vida das janelas ativas (varredura a cada ~480ms)
    if (elapsed - this._lastSweep > 480) {
      for (const w of ws.wins)
        if (w.state === 'ACTIVE' && w.aliveDur > 600) w.aliveDur = 600;
      this._lastSweep = elapsed;
    }

    // Pulsos desaceleram
    if (ps._fillAt < elapsed + 3000) ps._fillAt = elapsed + 3000 + Math.random() * 2500;

    // Dissolve blocos um por vez (a cada ~650ms)
    if (elapsed - this._lastFade > 650) {
      const alive = cs.blocks.filter(b => b.state !== 'FADING');
      if (alive.length > 1) { alive[0].startFading(elapsed); this._lastFade = elapsed; }
    }
  }

  // Modo ocioso: libera todos os sistemas — CogRhythm volta ao controle autônomo.
  _doIdle(ss, rh) {
    rh._extTarget    = undefined;
    ss._rhythmTarget = undefined;
  }
}

// ── CognitiveEngine ────────────────────────────────────────────────────────────
class CognitiveEngine {
  constructor(w, h) {
    this._frame      = 0;
    this._lastHud    = -9999;
    this._lastRepart = -9999;
    this._wavesDone  = 0;
    this._particles  = [];
    this._shapes     = [];
    this._holos      = [];
    this._hudPanels  = [];

    // Ritmo irregular de processos
    this._rhythmState = 'quiet';
    this._rhythmAt    = 0;
    this._rhythmDur   = 1800 + Math.random()*2000;
    this._burstLeft   = 0;
    this._nextHoloAt  = 2200;

    this.resize(w, h);
    this._codeSystem = new LiveCodeSystem(w, h, this.mob);
    this._cogWinSys    = new CogWinSystem(w, h, this.mob);
    this._cogPulseSys  = new CogPulseSystem(w, h, this.mob);
    this._structSys    = new StructSystem(w, h, this.mob);
    this._atmSys       = new AtmosphereSystem(w, h, this.mob);
    this._rhythm       = new CogRhythm();
    this._orchestrator = new CogOrchestrator();
    window._athenaCogSignal = (st, cx) => this._orchestrator.signal(st, cx ?? 0.5);
    this._preSeed();
  }

  resize(w, h) {
    this.w=w; this.h=h;
    this.mob = w < 768;
    this.cx  = w/2;
    this.cy  = h*0.46;
    this._particles = [];
    this._shapes    = [];
    this._wavesDone = 0;
    if (this._codeSystem) this._codeSystem.resize(w, h, this.mob);
    if (this._cogWinSys)   this._cogWinSys.resize(w, h, this.mob);
    if (this._cogPulseSys) this._cogPulseSys.resize(w, h, this.mob);
    if (this._structSys)   this._structSys.resize(w, h, this.mob);
    if (this._atmSys)      this._atmSys.resize(w, h, this.mob);
  }

  destroy() {
    this._particles=[]; this._shapes=[];
    this._holos=[];     this._hudPanels=[];
    window._athenaCogSignal = null;
  }

  _preSeed() {
    for (let i = 0; i < 6; i++) this._spawnShape(-(4000+Math.random()*10000));
    if (SHOW_TECHNICAL_PANELS) {
      this._spawnHudPanel(-8000);
      this._spawnHudPanel(-12000);
      if (!this.mob) this._spawnHudPanel(-5000);
    }
  }

  _wave(elapsed, n, maxR, isCore) {
    for (let i = 0; i < n; i++) {
      const ang  = Math.random()*Math.PI*2;
      const dist = maxR*Math.sqrt(Math.random());
      this._particles.push({
        x:   this.cx+Math.cos(ang)*dist,
        y:   this.cy+Math.sin(ang)*dist,
        vx:  (Math.random()-0.5)*(isCore?0.18:0.28),
        vy:  (Math.random()-0.5)*(isCore?0.18:0.28),
        r:   isCore ? Math.random()*2.4+1.0 : Math.random()*1.5+0.4,
        mop: isCore ? Math.random()*0.55+0.30 : Math.random()*0.22+0.08,
        col: Math.random()<0.84?'c': Math.random()<0.15?'m':'am',
        born:     elapsed,
        lifespan: 14000+Math.random()*20000,
        fi:700, fo:1400,
      });
    }
  }

  _spawnShape(elapsed) {
    const w=this.w, h=this.h, mob=this.mob;
    const types=['hex','hex','hex','diamond','diamond','diamond','tri'];
    const type =types[Math.floor(Math.random()*types.length)];
    const zones=[
      [w*(0.02+Math.random()*0.14), h*(0.06+Math.random()*0.35)],
      [w*(0.84+Math.random()*0.12), h*(0.06+Math.random()*0.35)],
      [w*(0.02+Math.random()*0.14), h*(0.55+Math.random()*0.38)],
      [w*(0.84+Math.random()*0.12), h*(0.55+Math.random()*0.38)],
      [w*(0.12+Math.random()*0.18), h*(0.03+Math.random()*0.12)],
      [w*(0.68+Math.random()*0.18), h*(0.03+Math.random()*0.12)],
      [w*(0.12+Math.random()*0.18), h*(0.83+Math.random()*0.12)],
      [w*(0.68+Math.random()*0.18), h*(0.83+Math.random()*0.12)],
      [w*(0.28+Math.random()*0.12), h*(0.08+Math.random()*0.22)],
      [w*(0.58+Math.random()*0.12), h*(0.08+Math.random()*0.22)],
    ];
    const [scx,scy]=zones[Math.floor(Math.random()*zones.length)];
    const isLarge=Math.random()<0.30;
    const r=isLarge?(mob?38+Math.random()*24:52+Math.random()*32):(mob?10+Math.random()*18:12+Math.random()*22);
    const scols=['c','c','c','c','am','am'];
    this._shapes.push({
      type, cx:scx, cy:scy, r,
      angle:    Math.random()*Math.PI*2,
      speed:    (Math.random()-0.5)*0.007,
      col:      scols[Math.floor(Math.random()*scols.length)],
      mop:      isLarge?0.07+Math.random()*0.08:0.12+Math.random()*0.14,
      born:     elapsed,
      lifespan: 5000+Math.random()*10000,
      fi:900, fo:1100,
    });
  }

  _spawnHolo(elapsed) {
    const w=this.w, h=this.h;
    const onLeft = Math.random()<0.5;
    const x = onLeft ? w*(0.02+Math.random()*0.28) : w*(0.62+Math.random()*0.34);
    const tp = ['bar','bar','bar','rect','datablock'][Math.floor(Math.random()*5)]; // barras mais comuns
    let bw, bh, fillDuration=0;
    if      (tp==='bar')  { bw=40+Math.random()*60; bh=4; fillDuration=1000+Math.random()*2000; }
    else if (tp==='rect') { bw=28+Math.random()*42; bh=18+Math.random()*22; }
    else                  { bw=48+Math.random()*38; bh=12+Math.random()*10; }
    this._holos.push({
      tp, x, bw, bh, fillDuration,
      y:          h*(0.10+Math.random()*0.70),
      col:        Math.random()<0.74?'c': Math.random()<0.18?'m':'am',
      mop:        0.24+Math.random()*0.30,
      fill:       Math.random(),
      label:      PROCESS_NAMES[Math.floor(Math.random()*PROCESS_NAMES.length)],
      completedAt: -1, // será setado ao completar
      born:       elapsed,
      lifespan:   1800+Math.random()*3000,
      fi:200, fo:400,
    });
  }

  _spawnHudPanel(elapsed) {
    const w=this.w, h=this.h, mob=this.mob;
    const pw=mob?w*0.44:w*0.27;
    const ph=mob?h*0.18:h*0.22;
    const corners=[
      {x:w*0.01,         y:h*0.04,        corner:0},
      {x:w-pw-w*0.01,    y:h*0.04,        corner:1},
      {x:w*0.01,         y:h-ph-h*0.06,   corner:2},
      {x:w-pw-w*0.01,    y:h-ph-h*0.06,   corner:3},
    ];
    const refT=elapsed>0?elapsed:0;
    const used=new Set(this._hudPanels.filter(p=>(refT-p.born)<p.lifespan).map(p=>p.corner));
    const avail=[0,1,2,3].filter(i=>!used.has(i));
    if (!avail.length) return;
    const c=avail[Math.floor(Math.random()*avail.length)];
    const {x,y,corner}=corners[c];
    const rowCount=mob?5:7;
    const col=Math.random()<0.70?'c':'am';
    this._hudPanels.push({
      corner, x, y, pw, ph, col,
      rows:     this._genHudRows(rowCount),
      mop:      0.13+Math.random()*0.10,
      rowDelay: 380+Math.random()*320,   // ms entre linhas
      typeDur:  180+Math.random()*200,   // ms para digitar cada linha
      born:     elapsed,
      lifespan: 13000+Math.random()*10000,
      fi:700, fo:1400,
    });
  }

  _genHudRows(n) {
    return Array.from({length:n}, ()=>[
      HUD_FUNCS[Math.floor(Math.random()*HUD_FUNCS.length)],
      HUD_STATUS[Math.floor(Math.random()*HUD_STATUS.length)],
      `${(55+Math.random()*44).toFixed(1)}%`,
      `${(0.4+Math.random()*9.5).toFixed(2)}ms`,
    ]);
  }

  // ── Sistema de ritmo irregular ─────────────────────────────────────────────
  _updateRhythm(elapsed) {
    if (elapsed < 1800) return;
    if (elapsed - this._rhythmAt <= this._rhythmDur) return;

    const r = Math.random();
    if (this._rhythmState === 'quiet') {
      if      (r < 0.38) { this._rhythmState='burst';  this._rhythmDur=1000+Math.random()*1500; this._burstLeft=2+Math.floor(Math.random()*2); }
      else if (r < 0.68) { this._rhythmState='steady'; this._rhythmDur=2500+Math.random()*3000; this._nextHoloAt=elapsed+1200+Math.random()*1000; }
      else               { this._rhythmDur=2000+Math.random()*3500; }
    } else if (this._rhythmState === 'burst') {
      if      (r < 0.52) { this._rhythmState='quiet';  this._rhythmDur=2000+Math.random()*4000; }
      else if (r < 0.78) { this._rhythmState='steady'; this._rhythmDur=2500+Math.random()*3000; this._nextHoloAt=elapsed+1200+Math.random()*1000; }
      else               { this._burstLeft=2+Math.floor(Math.random()*2); this._rhythmDur=800+Math.random()*1000; }
    } else { // steady
      if      (r < 0.40) { this._rhythmState='quiet';  this._rhythmDur=1800+Math.random()*3500; }
      else if (r < 0.65) { this._rhythmState='burst';  this._rhythmDur=1000+Math.random()*1500; this._burstLeft=2+Math.floor(Math.random()*2); }
      else               { this._rhythmDur=2000+Math.random()*3000; this._nextHoloAt=elapsed+1200+Math.random()*1000; }
    }
    this._rhythmAt = elapsed;
  }

  _spawnHoloByRhythm(elapsed) {
    if (elapsed < 1800) return;
    const maxHolos = this.mob ? 3 : 5;
    if (this._holos.length >= maxHolos) return;

    if (this._rhythmState === 'burst' && this._burstLeft > 0) {
      // Burst: cada processo nasce rapidamente (150-280ms de intervalo)
      const gap = 150 + this._burstLeft * 50;
      if (elapsed - (this._nextHoloAt - gap) > gap) {
        this._spawnHolo(elapsed);
        this._burstLeft--;
        this._nextHoloAt = elapsed + gap;
      }
    } else if (this._rhythmState === 'steady' && elapsed >= this._nextHoloAt) {
      // Steady: um processo a cada 1.2-2.4s
      this._spawnHolo(elapsed);
      this._nextHoloAt = elapsed + 1200 + Math.random() * 1200;
    }
    // quiet: nada
  }

  render(ctx, elapsed) {
    this._frame++;
    const f=this._frame;
    const w=this.w, h=this.h, cx=this.cx, cy=this.cy, mob=this.mob;

    // Pruning
    if (f%90===0) {
      this._particles = this._particles.filter(p  => (elapsed-p.born)  < p.lifespan  + 200);
      this._shapes    = this._shapes.filter(s     => (elapsed-s.born)  < s.lifespan  + 200);
      this._holos     = this._holos.filter(h2     => (elapsed-h2.born) < h2.lifespan + 200);
      this._hudPanels = this._hudPanels.filter(p  => (elapsed-p.born)  < p.lifespan  + 200);
    }

    // Ritmo
    this._updateRhythm(elapsed);
    if (SHOW_TECHNICAL_PANELS) this._spawnHoloByRhythm(elapsed);

    // Ondas de partículas
    const waves=[
      {t:0,    n:mob?18:22, r:mob?90:110,  core:true },
      {t:500,  n:mob?10:14, r:mob?180:240, core:false},
      {t:2500, n:mob?8:12,  r:mob?400:600, core:false},
      {t:5500, n:mob?8:12,  r:mob?150:200, core:true },
    ];
    for (let wi=this._wavesDone; wi<waves.length; wi++) {
      if (elapsed>=waves[wi].t) { this._wave(elapsed,waves[wi].n,waves[wi].r,waves[wi].core); this._wavesDone=wi+1; }
      else break;
    }
    if (this._wavesDone>=waves.length) {
      const alive=this._particles.filter(p=>(elapsed-p.born)<p.lifespan).length;
      const cap=mob?40:56;
      if (alive<cap*0.65 && elapsed-this._lastRepart>700) {
        this._wave(elapsed, mob?5:7, mob?200:280, false);
        this._lastRepart=elapsed;
      }
    }

    // Formas
    const shapeTarget=elapsed<200?6:elapsed<1000?9:mob?13:20;
    if (this._shapes.length<shapeTarget) this._spawnShape(elapsed);

    // HUD panels
    if (SHOW_TECHNICAL_PANELS) {
      const hudAlive=this._hudPanels.filter(p=>(elapsed-p.born)<p.lifespan).length;
      if (hudAlive<(mob?2:3) && elapsed-this._lastHud>7000) {
        this._spawnHudPanel(elapsed);
        this._lastHud=elapsed;
      }
    }

    // Completions recentes (para reação das partículas)
    const recentCompletions = this._holos.filter(h2 =>
      h2.completedAt > 0 && elapsed - h2.completedAt < 650
    );

    // ═══════════════════════════════════════════════════
    // DRAW
    // ═══════════════════════════════════════════════════
    ctx.clearRect(0, 0, w, h);

    // 1. Base translúcida: preserva contraste da animação e deixa visível o
    // vídeo de raciocínio que fica atrás do canvas.
    ctx.fillStyle='rgba(2,4,14,0.42)';
    ctx.fillRect(0,0,w,h);

    // 1.2. Ritmo Cognitivo — Camada 6 (define timings antes de todos os sistemas)
    const _aCt = this._codeSystem.blocks.filter(b => b.state !== 'FADING').length
      + this._cogWinSys.wins.filter(w2 => w2.state === 'REVEALING' || w2.state === 'ACTIVE').length;
    this._rhythm.update(elapsed);
    this._rhythm.applyTo(this._structSys, this._cogWinSys, this._cogPulseSys, elapsed);

    // 1.3. Orquestração Cognitiva — Camada 8 (após ritmo; sobrescreve com prioridade máxima)
    this._orchestrator.update(elapsed, this._codeSystem, this._cogWinSys, this._cogPulseSys, this._structSys, this._rhythm);

    // 1.5. Atmosfera — Camada 5 (pré-pass: luz ambiente driftante)
    this._atmSys.drawPre(ctx, elapsed);

    // 2. Glow suave do Orb
    const bg=ctx.createRadialGradient(cx,cy,0,cx,cy,Math.max(w,h)*0.65);
    bg.addColorStop(0,    this._C(0.038));
    bg.addColorStop(0.30, this._C(0.014));
    bg.addColorStop(0.70, this._B(0.006));
    bg.addColorStop(1,    'rgba(0,0,0,0)');
    ctx.fillStyle=bg; ctx.fillRect(0,0,w,h);

    // 3. Grid
    drawGrid(ctx, w, h, 46,  this._C(0.030));
    drawGrid(ctx, w, h, 116, this._B(0.018));

    // 3.5. Estrutura Cognitiva — Camada 4
    this._structSys.update(elapsed, _aCt);
    this._structSys.draw(ctx, elapsed);

    // 4. Quadros técnicos (desativados na composição visual limpa)
    if (SHOW_TECHNICAL_PANELS) {
      this._codeSystem.update(elapsed);
      this._codeSystem.draw(ctx, elapsed);
    }

    // 5. Formas geométricas
    ctx.lineWidth=0.8;
    for (const s of this._shapes) {
      s.angle+=s.speed;
      const op=ltOp(s.born,elapsed,s.lifespan,s.fi,s.fo)*s.mop;
      if (op<=0.001) continue;
      ctx.strokeStyle=this._col(s.col,op);
      ctx.beginPath();
      if      (s.type==='hex')     hexPath(ctx,s.cx,s.cy,s.r,s.angle);
      else if (s.type==='diamond') diamondPath(ctx,s.cx,s.cy,s.r,s.angle);
      else                         triPath(ctx,s.cx,s.cy,s.r,s.angle);
      ctx.stroke();
    }

    // 6–6.5. Telemetria e janelas técnicas (desativadas)
    if (SHOW_TECHNICAL_PANELS) {
      this._drawHudPanels(ctx, elapsed);
      this._cogWinSys.update(elapsed);
      this._cogWinSys.draw(ctx, elapsed);
    }

    // 7. Conexões neurais
    this._drawConns(ctx, elapsed);

    // 7.5. Pulsos dos quadros técnicos (desativados junto aos quadros)
    if (SHOW_TECHNICAL_PANELS) {
      this._cogPulseSys.scan(this._codeSystem.blocks, this._cogWinSys.wins, elapsed);
      this._cogPulseSys.update(elapsed);
      this._cogPulseSys.draw(ctx, elapsed);
    }

    // 8. Partículas (com reação a completions)
    for (const p of this._particles) {
      let op=ltOp(p.born,elapsed,p.lifespan,p.fi,p.fo)*p.mop;
      if (op<=0.003) continue;

      // Reação a conclusões de processo: partículas próximas brilham
      for (const hol of recentCompletions) {
        const dx=p.x-hol.x, dy=p.y-hol.y;
        const d=Math.sqrt(dx*dx+dy*dy);
        if (d<240) {
          const prox  = 1-d/240;
          const wave  = Math.sin(Math.PI*(elapsed-hol.completedAt)/650);
          op = Math.min(op*(1+0.75*prox*wave), 1.0);
        }
      }

      ctx.fillStyle=this._col(p.col, op);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI*2);
      ctx.fill();
      p.x=(p.x+p.vx+w)%w;
      p.y=(p.y+p.vy+h)%h;
    }

    // 9. Indicadores de processo com texto (desativados)
    if (SHOW_TECHNICAL_PANELS) this._drawHolos(ctx, elapsed);

    // 9.5. Atmosfera — Camada 5 (pós-pass: vinheta, névoa, influência do Orbe)
    this._atmSys.drawPost(ctx, elapsed, _aCt);

    // 10. Pulso do Orb
    const pulse=0.042+0.020*Math.sin(elapsed*0.0024);
    const ig=ctx.createRadialGradient(cx,cy,0,cx,cy,mob?148:198);
    ig.addColorStop(0,this._C(pulse)); ig.addColorStop(1,'rgba(0,0,0,0)');
    ctx.fillStyle=ig; ctx.fillRect(0,0,w,h);

    // 11. Glows de canto duplos
    if (elapsed>400) {
      const pct=Math.min((elapsed-400)/1400,1);
      const a1=ctx.createRadialGradient(w*0.02,h*0.97,0,w*0.02,h*0.97,mob?90:120);
      a1.addColorStop(0,this._A(0.38*pct)); a1.addColorStop(0.5,this._A(0.18*pct)); a1.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=a1; ctx.fillRect(0,0,w,h);
      const a2=ctx.createRadialGradient(w*0.02,h*0.97,0,w*0.02,h*0.97,mob?250:340);
      a2.addColorStop(0,this._A(0.14*pct)); a2.addColorStop(0.5,this._A(0.05*pct)); a2.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=a2; ctx.fillRect(0,0,w,h);
      const t1=ctx.createRadialGradient(w*0.99,h*0.02,0,w*0.99,h*0.02,mob?75:105);
      t1.addColorStop(0,this._C(0.11*pct)); t1.addColorStop(0.5,this._C(0.045*pct)); t1.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=t1; ctx.fillRect(0,0,w,h);
      const t2=ctx.createRadialGradient(w*0.99,h*0.02,0,w*0.99,h*0.02,mob?200:280);
      t2.addColorStop(0,this._C(0.038*pct)); t2.addColorStop(0.5,this._C(0.012*pct)); t2.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=t2; ctx.fillRect(0,0,w,h);
    }
  }

  // ── Holos com comportamento de processo ────────────────────────────────────
  _drawHolos(ctx, elapsed) {
    for (const hol of this._holos) {
      const op=ltOp(hol.born,elapsed,hol.lifespan,hol.fi,hol.fo)*hol.mop;
      if (op<=0.005) continue;
      ctx.lineWidth=0.7;

      if (hol.tp==='bar') {
        const fillPct=Math.min((elapsed-hol.born)/hol.fillDuration, 1.0);

        // Detecta conclusão (uma única vez)
        if (hol.completedAt<0 && fillPct>=0.99) hol.completedAt=elapsed;

        const completionAge = hol.completedAt>0 ? elapsed-hol.completedAt : -1;
        const flashPct = (completionAge>=0 && completionAge<550)
          ? Math.sin(Math.PI*completionAge/550) : 0;

        // Nome do processo (typewriter: aparece com a barra)
        const labelCharCount = Math.floor(fillPct*hol.label.length);
        const labelText = fillPct<1
          ? hol.label.slice(0,Math.max(1,labelCharCount))+'_'
          : hol.label;
        ctx.font='6px monospace';
        ctx.textAlign='left';
        const labelColor = flashPct>0
          ? `rgba(52,210,85,${Math.min(op*1.6*flashPct+op*0.6,0.85)})`
          : this._col(hol.col, op*0.70);
        ctx.fillStyle=labelColor;
        ctx.fillText(labelText, hol.x, hol.y-4);

        // Borda
        const borderCol = flashPct>0
          ? `rgba(52,210,85,${Math.min(op*(0.8+0.6*flashPct),0.90)})`
          : this._col(hol.col, op);
        ctx.strokeStyle=borderCol;
        ctx.strokeRect(hol.x, hol.y, hol.bw, hol.bh+6);

        // Fill progressivo da barra
        const fillCol = flashPct>0
          ? `rgba(52,210,85,${Math.min(op*(0.55+0.45*flashPct),0.85)})`
          : this._col(hol.col, op*0.55);
        ctx.fillStyle=fillCol;
        ctx.fillRect(hol.x+1, hol.y+1, (hol.bw-2)*fillPct, hol.bh+4);

      } else if (hol.tp==='rect') {
        ctx.strokeStyle=this._col(hol.col,op);
        ctx.strokeRect(hol.x, hol.y, hol.bw, hol.bh);
        ctx.beginPath();
        ctx.moveTo(hol.x, hol.y+9); ctx.lineTo(hol.x+hol.bw, hol.y+9);
        ctx.strokeStyle=this._col(hol.col,op*0.45); ctx.stroke();
      } else {
        ctx.fillStyle=this._col(hol.col, op*0.09);
        ctx.fillRect(hol.x, hol.y, hol.bw, hol.bh);
        ctx.strokeStyle=this._col(hol.col, op*0.55);
        ctx.strokeRect(hol.x, hol.y, hol.bw, hol.bh);
      }
    }
  }

  // ── HUD panels: typewriter linha por linha + highlight de conclusão ─────────
  _drawHudPanels(ctx, elapsed) {
    const mob=this.mob;
    for (const panel of this._hudPanels) {
      const op=ltOp(panel.born,elapsed,panel.lifespan,panel.fi,panel.fo)*panel.mop;
      if (op<=0.002) continue;
      const {x,y,pw,ph,rows,col,rowDelay,typeDur}=panel;
      const bOp=Math.min(op*1.8,0.72);

      // Fundo
      ctx.fillStyle=`rgba(1,4,18,${Math.min(op*6,0.90)})`;
      ctx.fillRect(x,y,pw,ph);

      // Borda
      ctx.strokeStyle=this._col(col,bOp);
      ctx.lineWidth=0.75;
      ctx.strokeRect(x,y,pw,ph);

      // Header bar
      ctx.fillStyle=this._col(col,op*0.28);
      ctx.fillRect(x,y,pw,16);
      ctx.beginPath();
      ctx.moveTo(x,y+16); ctx.lineTo(x+pw,y+16);
      ctx.strokeStyle=this._col(col,op*0.50);
      ctx.lineWidth=0.5; ctx.stroke();

      // Dots
      const dCols=[
        this._A(Math.min(op*2.8,1.0)),
        this._C(Math.min(op*2.4,0.95)),
        this._C(Math.min(op*1.4,0.52)),
      ];
      for (let d=0;d<3;d++) {
        ctx.fillStyle=dCols[d];
        ctx.beginPath();
        ctx.arc(x+7+d*9, y+8, 2.4, 0, Math.PI*2);
        ctx.fill();
      }

      // Linhas com typewriter linha a linha
      ctx.font=`${mob?7:8}px monospace`;
      ctx.textAlign='left';
      const rowH=(ph-18)/rows.length;
      const cW=pw/4.15;

      for (let r=0; r<rows.length; r++) {
        // Quando esta linha começa a aparecer
        const rowRevealAt = panel.born + panel.fi + r * rowDelay;
        const rowT = elapsed - rowRevealAt;
        if (rowT < 0) continue; // linha ainda não chegou

        // Fases de aparecimento: col0 digita (0→50%), col1 aparece (50-70%), col2+3 (70-100%)
        const p0 = Math.min(rowT / (typeDur*0.50), 1);
        const p1 = Math.min(Math.max((rowT-typeDur*0.50)/(typeDur*0.25),0),1);
        const p2 = Math.min(Math.max((rowT-typeDur*0.75)/(typeDur*0.25),0),1);

        const ry=y+18+r*rowH+rowH*0.68;

        // Highlight de conclusão por linha (aparece 2s depois de estar totalmente digitada)
        const completionDelay = typeDur + 2000 + r*180;
        const completionT = rowT - completionDelay;
        const completionAlpha = (completionT>0 && completionT<700)
          ? Math.sin(Math.PI*completionT/700) : 0;

        if (completionAlpha>0.01) {
          const hColor = rows[r][1]==='PROC'
            ? `rgba(255,160,30,${op*0.10*completionAlpha})`
            : `rgba(52,210,85,${op*0.10*completionAlpha})`;
          ctx.fillStyle=hColor;
          ctx.fillRect(x, y+18+r*rowH, pw, rowH);
        }

        // Linha alternada
        if (r%2===0) {
          ctx.fillStyle=`rgba(255,255,255,${op*0.05})`;
          ctx.fillRect(x, y+18+r*rowH, pw, rowH);
        }

        // Col 0: digita caractere a caractere
        if (p0>0) {
          const charCount=Math.floor(p0*rows[r][0].length);
          const cursor   = p0<1 ? '_' : (rowT-typeDur<900 && Math.floor(elapsed/240)%2===0 ? '_' : '');
          const col0text = rows[r][0].slice(0,charCount)+cursor;
          // Highlight de conclusão altera a cor da col0
          let col0color;
          if (completionAlpha>0.01) {
            const ratio=completionAlpha;
            col0color = rows[r][1]==='PROC'
              ? `rgba(255,160,30,${Math.min(op*1.8+ratio*op,0.85)})`
              : `rgba(52,210,85,${Math.min(op*1.8+ratio*op,0.85)})`;
          } else {
            col0color = this._col(col, Math.min(op*1.8,0.85));
          }
          ctx.fillStyle=col0color;
          ctx.fillText(col0text, x+4, ry);
        }

        // Col 1 (status): aparece gradualmente
        if (p1>0) {
          const v=rows[r][1];
          let fs;
          if      (v==='ACTIVE') fs=this._C(Math.min(op*2.0*p1,0.80));
          else if (v==='PROC')   fs=`rgba(255,160,30,${Math.min(op*2.2*p1,0.82)})`;
          else if (v==='WAIT')   fs=`rgba(255,160,30,${Math.min(op*1.5*p1,0.60)})`;
          else                    fs=this._col(col, Math.min(op*1.3*p1,0.70));
          ctx.fillStyle=fs;
          ctx.fillText(rows[r][1], x+4+cW, ry);
        }

        // Col 2 e 3: aparecem juntas no final
        if (p2>0) {
          ctx.fillStyle=this._col(col,Math.min(op*(1.0+(r%3)*0.28)*p2,0.70));
          ctx.fillText(rows[r][2], x+4+2*cW, ry);
          ctx.fillStyle=this._col(col,Math.min(op*(0.8+(r%3)*0.22)*p2,0.65));
          ctx.fillText(rows[r][3], x+4+3*cW, ry);
        }
      }
    }
  }

  // ── Conexões neurais batched ────────────────────────────────────────────────
  _drawConns(ctx, elapsed) {
    const pts=this._particles;
    if (pts.length<2) return;
    const near=[],mid=[],far_=[];
    for (let i=0;i<pts.length;i++) {
      const oa=ltOp(pts[i].born,elapsed,pts[i].lifespan,pts[i].fi,pts[i].fo);
      if (oa<=0) continue;
      for (let j=i+1;j<pts.length;j++) {
        const ob=ltOp(pts[j].born,elapsed,pts[j].lifespan,pts[j].fi,pts[j].fo);
        if (ob<=0) continue;
        const dx=pts[i].x-pts[j].x, dy=pts[i].y-pts[j].y;
        const d2=dx*dx+dy*dy;
        if      (d2<3600)  near.push(i,j);
        else if (d2<10000) mid.push(i,j);
        else if (d2<25600) far_.push(i,j);
      }
    }
    ctx.lineWidth=0.5;
    const drawBatch=(batch,alpha)=>{
      if (!batch.length) return;
      ctx.beginPath();
      for (let k=0;k<batch.length;k+=2) {
        ctx.moveTo(pts[batch[k]].x, pts[batch[k]].y);
        ctx.lineTo(pts[batch[k+1]].x, pts[batch[k+1]].y);
      }
      ctx.strokeStyle=this._C(alpha); ctx.stroke();
    };
    drawBatch(near, 0.22);
    drawBatch(mid,  0.13);
    drawBatch(far_, 0.060);
  }

  _C(a){return `rgba(0,200,212,${a.toFixed(3)})`;}
  _B(a){return `rgba(60,150,255,${a.toFixed(3)})`;}
  _M(a){return `rgba(255,0,180,${a.toFixed(3)})`;}
  _A(a){return `rgba(255,160,30,${a.toFixed(3)})`;}
  _col(c,a){
    if(c==='b')  return this._B(a);
    if(c==='m')  return this._M(a);
    if(c==='am') return this._A(a);
    return this._C(a);
  }
}

// ── React component ────────────────────────────────────────────────────────────
export const AthenaCognitiveSpace = memo(function AthenaCognitiveSpace() {
  const canvasRef=useRef(null);
  const engineRef=useRef(null);
  const rafRef   =useRef(null);

  useEffect(()=>{
    const canvas=canvasRef.current;
    if(!canvas) return;
    const ctx=canvas.getContext('2d');
    canvas.width =window.innerWidth;
    canvas.height=window.innerHeight;

    const engine=new CognitiveEngine(canvas.width, canvas.height);
    engineRef.current=engine;

    const resize=()=>{
      canvas.width =window.innerWidth;
      canvas.height=window.innerHeight;
      engine.resize(canvas.width, canvas.height);
    };
    window.addEventListener('resize', resize);

    const startAt=Date.now();
    function loop(){
      rafRef.current=requestAnimationFrame(loop);
      engine.render(ctx, Date.now()-startAt);
    }
    loop();

    return ()=>{
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      engine.destroy();
    };
  },[]);

  return (
    <canvas
      ref={canvasRef}
      style={{position:'absolute', inset:0, display:'block'}}
    />
  );
});
